import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { 
  X, 
  Bookmark, 
  PlusCircle, 
  Settings, 
  FileText, 
  Info, 
  MessageSquare, 
  Shield, 
  LogOut,
  ChevronRight,
  User
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Profile Avatar component
function MenuProfileAvatar({ profile, user, size = 'lg' }) {
  const [imgFailed, setImgFailed] = React.useState(false);
  const sizeClasses = {
    lg: 'w-16 h-16 text-2xl'
  };

  const initial = (profile?.display_name || user?.email || 'U').charAt(0).toUpperCase();

  if (profile?.avatar_url && !imgFailed) {
    return (
      <img
        src={profile.avatar_url}
        alt={profile?.display_name || 'Profile'}
        className={`${sizeClasses[size]} rounded-full object-cover border-2 border-dive-gray`}
        onError={() => setImgFailed(true)}
      />
    );
  }

  return (
    <div className={`${sizeClasses[size]} rounded-full bg-dive-neon/20 flex items-center justify-center font-medium text-dive-neon border-2 border-dive-gray`}>
      {initial}
    </div>
  );
}

function HamburgerMenu({ isOpen, onClose }) {
  const { user, profile, isAdmin, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Close menu when route changes
  useEffect(() => {
    onClose();
  }, [location.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleLogout = async () => {
    try {
      await signOut();
      onClose();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleProfileClick = () => {
    onClose();
    if (user) {
      navigate('/profile');
    } else {
      navigate('/auth');
    }
  };

  const menuItems = [
    { to: '/lists', icon: Bookmark, label: 'Lists' },
    { to: '/add', icon: PlusCircle, label: 'Add a Bar' },
    { to: '/settings', icon: Settings, label: 'Settings' },
    { to: '/terms', icon: FileText, label: 'Terms of Service' },
    { to: '/privacy', icon: FileText, label: 'Privacy Policy' },
    { to: '/about', icon: Info, label: 'About Us' },
    { to: '/feedback', icon: MessageSquare, label: 'Report Bug / Feedback' },
  ];

  // Add admin item if user is admin
  if (isAdmin) {
    menuItems.push({ to: '/admin', icon: Shield, label: 'Admin Dashboard', isAdmin: true });
  }

  const menuContent = (
    <>
      {/* Overlay */}
      <div 
        className={`fixed inset-0 bg-black/60 z-[3000] transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />
      
      {/* Menu Panel */}
      <div 
        className={`fixed top-0 right-0 h-full w-[80%] max-w-[320px] bg-dive-darker z-[3001] transform transition-transform duration-300 ease-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute right-4 p-2 text-gray-400 hover:text-white transition-colors z-10"
          style={{ top: 'calc(1rem + env(safe-area-inset-top, 0px))' }}
          aria-label="Close menu"
        >
          <X className="w-6 h-6" />
        </button>

        {/* User Header */}
        <div 
          onClick={handleProfileClick}
          className="bg-dive-dark/50 px-6 py-8 flex flex-col items-center cursor-pointer hover:bg-dive-dark/70 transition-colors border-b border-dive-gray"
        >
          {user ? (
            <>
              <MenuProfileAvatar profile={profile} user={user} />
              <h3 className="text-white font-medium mt-3 text-lg">
                {profile?.display_name || 'User'}
              </h3>
              <p className="text-gray-400 text-sm">
                @{profile?.username || 'username'}
              </p>
              <p className="text-dive-neon text-xs mt-1 flex items-center gap-1">
                View Profile <ChevronRight className="w-3 h-3" />
              </p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-full bg-dive-gray flex items-center justify-center border-2 border-dive-gray">
                <User className="w-8 h-8 text-gray-500" />
              </div>
              <h3 className="text-white font-medium mt-3 text-lg">
                Guest
              </h3>
              <p className="text-dive-neon text-xs mt-1 flex items-center gap-1">
                Sign In <ChevronRight className="w-3 h-3" />
              </p>
            </>
          )}
        </div>

        {/* Menu Items */}
        <div className="flex-1 overflow-y-auto py-2">
          {menuItems.map(({ to, icon: Icon, label, isAdmin: isAdminItem }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-4 px-6 py-4 transition-colors ${
                  isActive
                    ? 'bg-dive-neon/10 text-dive-neon border-r-2 border-dive-neon'
                    : isAdminItem
                      ? 'text-dive-amber hover:bg-dive-gray/30'
                      : 'text-gray-300 hover:bg-dive-gray/30 hover:text-white'
                }`
              }
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              <span className="flex-1 font-medium">{label}</span>
              <ChevronRight className="w-4 h-4 text-gray-500" />
            </NavLink>
          ))}
        </div>

        {/* Divider and Logout */}
        {user && (
          <div className="border-t border-dive-gray">
            <button
              onClick={handleLogout}
              className="flex items-center gap-4 px-6 py-4 w-full text-red-400 hover:bg-red-500/10 transition-colors"
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              <span className="flex-1 font-medium text-left">Log Out</span>
            </button>
          </div>
        )}
      </div>
    </>
  );

  return createPortal(menuContent, document.body);
}

export default HamburgerMenu;
